import { QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from './api.js';

/**
 * One `QueryClient` for the app, with the three defaults that matter for a PBX console.
 *
 * ── 1. DO NOT RETRY A 4xx (OR A 501) ─────────────────────────────────────────
 * TanStack Query retries three times by default, and for this API that is wrong in both
 * directions. A 403 will be a 403 on the fourth attempt too, so the retries only delay the
 * error message by a couple of seconds — and a 429 from the login throttle gets *worse* when
 * retried, because each attempt counts. A 5xx or a transport failure is genuinely worth
 * retrying, and those are the ones left in — except 501 Not Implemented, which is as final as
 * a 403 (see the predicate).
 *
 * A 401 is excluded for a different reason: `api.ts` has already refreshed and retried once
 * by the time the error reaches here, so a 401 arriving at this layer means the session is
 * over. Retrying it would fire three more refreshes at a server that has already said no.
 *
 * ── 2. THE CACHE HOLDS LIVE TELEPHONY STATE, SO IT IS SHORT-LIVED ────────────
 * A channel list that is thirty seconds old is not stale data, it is WRONG data — the call it
 * shows has ended. `staleTime` is therefore small and `refetchOnWindowFocus` is left ON,
 * which is exactly what an operator alt-tabbing back to the console wants.
 *
 * ── 3. AN EXPIRED SESSION IS NOT A PER-QUERY ERROR ───────────────────────────
 * The `QueryCache` handler is global on purpose. Every page would otherwise need its own
 * "was this a 401?" branch, and the one that forgot would render an error card behind a
 * session that no longer exists.
 */
export function createQueryClient(onSessionLost: () => void): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof ApiError && error.status === 401) onSessionLost();
      },
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          // 501 is the one 5xx that is a DECISION, not a failure: the `/cdr` group answers it
          // whenever SINGLE_TENANT_MODE is off, and will answer it on every retry. Retrying
          // held the call history on a skeleton for ~3 s, three requests, before the page
          // could say what is switched off — the "it does not spin" the IA asks for.
          if (error instanceof ApiError && error.status === 501) return false;
          return failureCount < 2;
        },
        // 10s: long enough that navigating between two pages showing the same list does not
        // re-fetch it, short enough that nothing on screen is meaningfully out of date.
        staleTime: 10_000,
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Never. A retried POST is a second extension, a second call, a second delete — and
        // this API is not idempotent by design.
        retry: false,
      },
    },
  });
}

/**
 * Empty the cache whenever the signed-in PERSON changes — and only then.
 *
 * ── THE CACHE IS PER-PERSON DATA, AND SIGN-OUT DOES NOT RELOAD THE PAGE ──────
 * Sign-out is SPA-only (`logout()` → `/login` → sign-in → `navigate('/')`), and nothing used to
 * clear the cache in between, so the next person to sign in on the same tab inherited the last
 * one's cached answers. The dangerous one is `['softphone-credentials']`: `staleTime: Infinity`
 * and no refetch on focus (`SoftphoneProvider` says why), so if A signed out and B signed in
 * within the five-minute gcTime, B's browser phone registered with A's SIP credentials — B's
 * calls went out as A, and A's calls rang at B. `['auth-me']`, `['my-caller-id', …]` and
 * `['queues-me']` carried over the same way.
 *
 * ── ON A CHANGE OF PERSON, NOT ON EVERY SESSION EVENT ─────────────────────────
 * `onSessionChange` also fires on every 15-minute token refresh, with the SAME person. Clearing
 * then would drop the softphone's credentials mid-shift and remount it, tearing down a call in
 * progress. So the id is compared: A → A is a refresh and keeps everything; A → nobody (sign
 * out, or a refresh the server refused) and nobody → B clear.
 *
 * Takes the subscription as a parameter so the rule can be tested without a real session;
 * `main.tsx` passes `onSessionChange` and registers this BEFORE the router's listener, so the
 * cache is already empty when the router re-runs its guards.
 */
export function clearCacheWhenPersonChanges(
  queryClient: QueryClient,
  subscribe: (listener: (user: { id: string } | undefined) => void) => () => void,
): () => void {
  let lastUserId: string | undefined;
  return subscribe((user) => {
    if (user?.id === lastUserId) return;
    lastUserId = user?.id;
    queryClient.clear();
  });
}
